/**
 * Scheduling service for managing meetings and appointments
 */

import api from './api';

// Types and interfaces
export interface SalesContact {
  name: string;
  phone: string;
  email?: string;
  role?: string;
  specialties?: string[];
}

export interface ScheduleWindow {
  enabled: boolean;
  start_time: string | null;
  end_time: string | null;
}

export type WeeklySchedule = Record<string, ScheduleWindow>;

export interface SchedulingConfig {
  id: number;
  project_code: string;
  project_name: string;
  showroom_location: string;
  showroom_address?: string;
  booking_start_date?: string | null;
  booking_end_date?: string | null;
  open_booking_dates?: string[];
  custom_booking_message?: string | null;
  available_days: number[];
  start_time: string;
  end_time: string;
  weekly_schedule?: WeeklySchedule;
  holiday_schedule?: ScheduleWindow;
  slot_duration: number;
  buffer_time: number;
  slot_capacity: number;
  max_daily_meetings: number;
  advance_booking_days: number;
  sales_team_contacts: SalesContact[];
  special_instructions?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by: string;
}

export interface SchedulingConfigCreate {
  project_code: string;
  project_name: string;
  showroom_location: string;
  showroom_address?: string;
  booking_start_date?: string | null;
  booking_end_date?: string | null;
  open_booking_dates?: string[];
  custom_booking_message?: string | null;
  available_days: number[];
  start_time: string;
  end_time: string;
  weekly_schedule?: WeeklySchedule;
  holiday_schedule?: ScheduleWindow;
  slot_duration?: number;
  buffer_time?: number;
  slot_capacity?: number;
  max_daily_meetings?: number;
  advance_booking_days?: number;
  sales_team_contacts: SalesContact[];
  special_instructions?: string;
  is_active?: boolean;
}

export interface SchedulingConfigUpdate {
  project_name?: string;
  showroom_location?: string;
  showroom_address?: string;
  booking_start_date?: string | null;
  booking_end_date?: string | null;
  open_booking_dates?: string[];
  custom_booking_message?: string | null;
  available_days?: number[];
  start_time?: string;
  end_time?: string;
  weekly_schedule?: WeeklySchedule;
  holiday_schedule?: ScheduleWindow;
  slot_duration?: number;
  buffer_time?: number;
  slot_capacity?: number;
  max_daily_meetings?: number;
  advance_booking_days?: number;
  sales_team_contacts?: SalesContact[];
  special_instructions?: string;
  is_active?: boolean;
}

export enum MeetingStatus {
  SCHEDULED = 'scheduled',
  CONFIRMED = 'confirmed',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
  NO_SHOW = 'no_show'
}

export enum MeetingType {
  SHOWROOM_VISIT = 'showroom_visit',
  VIRTUAL_TOUR = 'virtual_tour',
  CONSULTATION = 'consultation'
}

export interface ScheduledMeeting {
  id: number;
  user_id: string;
  customer_name?: string | null;
  customer_first_name?: string | null;
  customer_last_name?: string | null;
  customer_display_name?: string | null;
  customer_phone: string;
  customer_email?: string;
  hubspot_contact_id?: string | null;
  project_code: string;
  project_name: string;
  meeting_date: string;
  booking_mode?: 'timed' | 'open_day';
  visit_window_start?: string | null;
  visit_window_end?: string | null;
  meeting_time: string | null;
  meeting_datetime: string | null;
  showroom_location: string;
  showroom_address?: string;
  sales_contact_name: string;
  sales_contact_phone: string;
  sales_contact_email?: string;
  meeting_type: MeetingType;
  status: MeetingStatus;
  customer_notes?: string;
  internal_notes?: string;
  lead_score?: number;
  conversation_id?: string;
  booking_source: string;
  created_at: string;
  updated_at: string;
  cancelled_at?: string;
  completed_at?: string;
  cancellation_reason?: string;
}

export interface AvailableSlot {
  slot_datetime: string;
  slot_time: string;
  is_available: boolean;
  remaining_capacity?: number;
  slot_capacity?: number;
  booked_count?: number;
  showroom_location: string;
  sales_contact: SalesContact;
}

export interface DailyAvailability {
  booking_mode?: 'timed' | 'open_day';
  is_bookable?: boolean;
  opening_time?: string | null;
  closing_time?: string | null;
  date: string;
  total_slots: number;
  available_slots: number;
  booked_slots: number;
  blocked_slots: number;
  slots: AvailableSlot[];
}

export interface SchedulingAnalytics {
  total_bookings: number;
  confirmed_bookings: number;
  completion_rate: number;
  cancellation_rate: number;
  no_show_rate: number;
  avg_customer_satisfaction: number;
  avg_lead_score: number;
  booking_trends: Array<{ date: string; bookings: number }>;
  popular_time_slots: Array<{ hour: number; time_display: string; booking_count: number }>;
  project_performance: Array<{
    project_code: string;
    project_name: string;
    total_bookings: number;
    completion_rate: number;
    cancellation_rate: number;
    no_show_rate: number;
  }>;
}

export type SortDirection = 'asc' | 'desc';

export type MeetingSortField =
  | 'meeting_datetime'
  | 'customer'
  | 'hubspot'
  | 'project'
  | 'showroom_location'
  | 'status'
  | 'sales_contact_name';

export interface MeetingFilter {
  project_codes?: string[];
  showroom_locations?: string[];
  statuses?: MeetingStatus[];
  start_date?: string;
  end_date?: string;
  sort_by?: MeetingSortField;
  sort_direction?: SortDirection;
}

export type MeetingExportFormat = 'excel';

export interface MeetingExportRequest {
  format: MeetingExportFormat;
  filters?: MeetingFilter;
}

export interface MeetingBookingRequest {
  user_id: string;
  customer_name: string;
  customer_phone: string;
  customer_email?: string;
  project_code: string;
  preferred_datetime?: string;
  preferred_date?: string;
  meeting_type: MeetingType;
  customer_notes?: string;
  conversation_id?: string;
}

export interface MeetingRescheduleRequest {
  meeting_id: number;
  new_datetime?: string;
  new_date?: string;
  reason?: string;
  notify_customer?: boolean;
}

export interface RecalculateTimeslotsResponse {
  message: string;
  configs_processed: number;
}

export interface SchedulingCopyPreviewRequest {
  project_name?: string;
  booking_start_date?: string | null;
  booking_end_date?: string | null;
  open_booking_dates?: string[];
  custom_booking_message?: string | null;
  available_days?: number[];
  start_time?: string;
  end_time?: string;
  weekly_schedule?: WeeklySchedule;
  holiday_schedule?: ScheduleWindow;
}

export interface SchedulingCopyPreviewResponse {
  booking_intro_text?: string;
  open_booking_text?: string;
  preview_text: string;
  opening_hours_text: string;
  weekly_schedule: WeeklySchedule;
  holiday_schedule: ScheduleWindow;
}

class SchedulingService {
  // Scheduling Configuration methods
  async getSchedulingConfigs(projectCodes?: string[], activeOnly: boolean = true): Promise<SchedulingConfig[]> {
    const params = new URLSearchParams();
    if (projectCodes && projectCodes.length > 0) {
      projectCodes.forEach(code => params.append('project_codes', code));
    }
    params.append('active_only', activeOnly.toString());

    const response = await api.get(`/scheduling/configs?${params.toString()}`);
    return response.data;
  }

  async getSchedulingConfig(configId: number): Promise<SchedulingConfig> {
    const response = await api.get(`/scheduling/configs/${configId}`);
    return response.data;
  }

  async getSchedulingConfigByProject(projectCode: string): Promise<SchedulingConfig> {
    const response = await api.get(`/scheduling/configs/project/${projectCode}`);
    return response.data;
  }

  async createSchedulingConfig(config: SchedulingConfigCreate): Promise<SchedulingConfig> {
    const response = await api.post('/scheduling/configs', config);
    return response.data;
  }

  async updateSchedulingConfig(configId: number, updates: SchedulingConfigUpdate): Promise<SchedulingConfig> {
    const response = await api.put(`/scheduling/configs/${configId}`, updates);
    return response.data;
  }

  async previewSchedulingCopy(payload: SchedulingCopyPreviewRequest): Promise<SchedulingCopyPreviewResponse> {
    const response = await api.post('/scheduling/configs/preview-copy', payload);
    return response.data;
  }

  async deleteSchedulingConfig(configId: number): Promise<void> {
    await api.delete(`/scheduling/configs/${configId}`);
  }

  // Availability methods
  async getAvailability(projectCode: string, startDate: string, endDate?: string): Promise<DailyAvailability[]> {
    const params = new URLSearchParams();
    params.append('start_date', startDate);
    if (endDate) {
      params.append('end_date', endDate);
    }

    const response = await api.get(`/scheduling/availability/${projectCode}?${params.toString()}`);
    return response.data;
  }

  // Meeting methods
  async getScheduledMeetings(
    filters?: MeetingFilter,
    skip: number = 0,
    limit: number = 100
  ): Promise<ScheduledMeeting[]> {
    const params = new URLSearchParams();
    
    if (filters) {
      if (filters.project_codes) {
        filters.project_codes.forEach(code => params.append('project_codes', code));
      }
      if (filters.showroom_locations) {
        filters.showroom_locations.forEach(location => params.append('showroom_locations', location));
      }
      if (filters.statuses) {
        filters.statuses.forEach(status => params.append('statuses', status));
      }
      if (filters.start_date) {
        params.append('start_date', filters.start_date);
      }
      if (filters.end_date) {
        params.append('end_date', filters.end_date);
      }
      if (filters.sort_by) {
        params.append('sort_by', filters.sort_by);
      }
      if (filters.sort_direction) {
        params.append('sort_direction', filters.sort_direction);
      }
    }
    
    params.append('skip', skip.toString());
    params.append('limit', limit.toString());

    const response = await api.get(`/scheduling/meetings?${params.toString()}`);
    return response.data;
  }

  async exportMeetings(exportRequest: MeetingExportRequest): Promise<Blob> {
    const response = await api.post('/scheduling/meetings/export', exportRequest, {
      responseType: 'blob',
    });
    return response.data;
  }

  async getScheduledMeeting(meetingId: number): Promise<ScheduledMeeting> {
    const response = await api.get(`/scheduling/meetings/${meetingId}`);
    return response.data;
  }

  async deleteMeeting(meetingId: number): Promise<{ message: string; meeting_id: number }> {
    const response = await api.delete(`/scheduling/meetings/${meetingId}`);
    return response.data;
  }

  async updateMeetingStatus(
    meetingId: number,
    status: MeetingStatus,
    notes?: string,
    cancellationReason?: string
  ): Promise<void> {
    const data: any = { status };
    if (notes) data.notes = notes;
    if (cancellationReason) data.cancellation_reason = cancellationReason;

    await api.put(`/scheduling/meetings/${meetingId}/status`, data);
  }

  async rescheduleMeeting(meetingId: number, rescheduleRequest: Omit<MeetingRescheduleRequest, 'meeting_id'>): Promise<any> {
    const response = await api.post(`/scheduling/meetings/${meetingId}/reschedule`, rescheduleRequest);
    return response.data;
  }

  async bookMeeting(bookingRequest: MeetingBookingRequest): Promise<any> {
    const response = await api.post('/scheduling/meetings/book', bookingRequest);
    return response.data;
  }

  // Analytics methods
  async getSchedulingAnalytics(
    projectCodes?: string[],
    startDate?: string,
    endDate?: string
  ): Promise<SchedulingAnalytics> {
    const params = new URLSearchParams();
    
    if (projectCodes && projectCodes.length > 0) {
      projectCodes.forEach(code => params.append('project_codes', code));
    }
    if (startDate) {
      params.append('start_date', startDate);
    }
    if (endDate) {
      params.append('end_date', endDate);
    }

    const response = await api.get(`/scheduling/analytics?${params.toString()}`);
    return response.data;
  }

  async getProjectSchedulingAnalytics(
    projectCode: string,
    startDate?: string,
    endDate?: string
  ): Promise<SchedulingAnalytics> {
    const params = new URLSearchParams();
    if (startDate) {
      params.append('start_date', startDate);
    }
    if (endDate) {
      params.append('end_date', endDate);
    }

    const response = await api.get(`/scheduling/analytics/project/${projectCode}?${params.toString()}`);
    return response.data;
  }

  // Dashboard methods
  async getDashboardSummary(): Promise<any> {
    const response = await api.get('/scheduling/dashboard/summary');
    return response.data;
  }

  async getUpcomingMeetings(daysAhead: number = 7, limit: number = 50): Promise<any> {
    const params = new URLSearchParams();
    params.append('days_ahead', daysAhead.toString());
    params.append('limit', limit.toString());

    const response = await api.get(`/scheduling/dashboard/upcoming?${params.toString()}`);
    return response.data;
  }

  // Notification methods
  async sendMeetingNotification(
    meetingId: number,
    notificationType: string,
    channel: string,
    customMessage?: string
  ): Promise<any> {
    const data: any = {
      notification_type: notificationType,
      channel: channel
    };
    if (customMessage) {
      data.custom_message = customMessage;
    }

    const response = await api.post(`/scheduling/meetings/${meetingId}/notifications/send`, data);
    return response.data;
  }

  async scheduleMeetingReminders(meetingId: number): Promise<any> {
    const response = await api.post(`/scheduling/meetings/${meetingId}/reminders/schedule`);
    return response.data;
  }

  async getNotificationTemplates(): Promise<any> {
    const response = await api.get('/scheduling/notifications/templates');
    return response.data;
  }

  async getNotificationAnalytics(
    startDate?: string,
    endDate?: string,
    projectCodes?: string[]
  ): Promise<any> {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (projectCodes) {
      projectCodes.forEach(code => params.append('project_codes', code));
    }

    const response = await api.get(`/scheduling/notifications/analytics?${params.toString()}`);
    return response.data;
  }

  async getNotificationStatus(): Promise<any> {
    const response = await api.get('/scheduling/notifications/status');
    return response.data;
  }

  async getMeetingNotifications(meetingId: number): Promise<any> {
    const response = await api.get(`/scheduling/meetings/${meetingId}/notifications`);
    return response.data;
  }

  async processPendingNotifications(): Promise<any> {
    const response = await api.post('/scheduling/notifications/process-pending');
    return response.data;
  }

  async retryFailedNotifications(maxAgeHours: number = 24): Promise<any> {
    const params = new URLSearchParams();
    params.append('max_age_hours', maxAgeHours.toString());

    const response = await api.post(`/scheduling/notifications/retry-failed?${params.toString()}`);
    return response.data;
  }

  async recalculateTimeslots(): Promise<RecalculateTimeslotsResponse> {
    const response = await api.post('/scheduling/recalculate-timeslots');
    return response.data;
  }
}

export const schedulingService = new SchedulingService();
